import {
  RAW_MATERIAL_KIND_LABELS,
  type RawMaterialKind,
  type StockOpnameType,
} from "@artisancode/api-types";
import { PackagePlus } from "lucide-react";

import { SearchableSelect } from "@/components/shared/searchable-select";
import { useProducts } from "@/hooks/use-products";
import { useRawMaterials } from "@/hooks/use-raw-materials";

interface Props {
  type: StockOpnameType;
  /** Items already in the session; hidden from the picker. */
  existingItemIds: Set<string>;
  onAdd: (itemId: string) => void;
  disabled?: boolean;
}

// ponytail: loads up to 500 items and filters client-side (SearchableSelect).
// Switch to server search (Combobox + `q`) if a workspace outgrows that.
const PICKER_LIMIT = 500;

export function StockOpnameAddItem({ type, ...props }: Props) {
  return type === "raw_material" ? (
    <RawMaterialPicker {...props} />
  ) : (
    <ProductPicker {...props} />
  );
}

function RawMaterialPicker(props: Omit<Props, "type">) {
  const { data } = useRawMaterials({ per_page: PICKER_LIMIT, is_active: true });
  return <Picker items={data?.items ?? []} {...props} />;
}

function ProductPicker(props: Omit<Props, "type">) {
  const { data } = useProducts({ per_page: PICKER_LIMIT });
  return <Picker items={data?.items ?? []} {...props} />;
}

function Picker({
  items,
  existingItemIds,
  onAdd,
  disabled,
}: Omit<Props, "type"> & {
  items: {
    id: string;
    name: string;
    sku?: string | null;
    kind?: RawMaterialKind;
  }[];
}) {
  const options = items
    .filter((i) => !existingItemIds.has(i.id))
    .map((i) => {
      const hintParts = [
        i.sku,
        i.kind ? RAW_MATERIAL_KIND_LABELS[i.kind] : null,
      ].filter(Boolean);
      return {
        value: i.id,
        label: i.name,
        hint: hintParts.length > 0 ? hintParts.join(" • ") : undefined,
      };
    });

  return (
    <div className="relative">
      <PackagePlus className="pointer-events-none absolute left-2.5 top-1/2 z-10 h-4 w-4 -translate-y-1/2 text-primary" />
      <SearchableSelect
        value=""
        onChange={(id) => id && onAdd(id)}
        options={options}
        placeholder="Cari & tambah barang untuk dihitung..."
        emptyText="Barang tidak ditemukan atau sudah ditambahkan."
        clearable={false}
        disabled={disabled}
        className="h-10 bg-background pl-8 text-sm"
      />
    </div>
  );
}
